import { useEffect, useState } from "react";
import {
  getSolicitudesRecibidas,
  aceptarSolicitud,
  rechazarSolicitud,
} from "../services/api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { useWebSocketActions } from "../context/WebSocketContext"; // 👈 Añadir
import { useTranslation } from "react-i18next";
import countriesData from "../data/countries";
import {CalendarDays, UsersRound } from "lucide-react";



const getCountryFlag = (countryCode) => {
  if (typeof countryCode !== "string") return "";

  const code = countryCode.trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) return "";

  return String.fromCodePoint(
    ...Array.from(code).map(
      (letter) => 127397 + letter.charCodeAt(0)
    )
  );
};

const getCountryLabel = (code) => {
  const found = countriesData.find(
    (c) => c.countryCode === code
  );
  return found?.countryLabel || code;
};

const getCityLabel = (cityCode, countryCode) => {
  const country = countriesData.find(
    (c) => c.countryCode === countryCode
  );

  return (
    country?.cityCodes.find(
      (city) => city.toLowerCase() === cityCode?.toLowerCase()
    ) || cityCode
  );
};

const Solicitudes = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { fetchNotificaciones } = useNotification();
  const { t } = useTranslation();
  const { addListener } = useWebSocketActions(); // 👈 Hook del WS

  const fetchSolicitudes = async () => {
    try {
      const res = await getSolicitudesRecibidas();
      setSolicitudes(res.data);
    } catch (err) {
      console.error("Error al cargar solicitudes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitudes();
  }, []);

  useEffect(() => {
    // 🎧 Suscribirse a nuevas solicitudes por WebSocket
    const remove = addListener((data) => {
      if (data.type === "notification" && data.data.type === "new_friend_request") {
        console.log("📬 Nueva solicitud recibida vía WebSocket");
        fetchSolicitudes();
        fetchNotificaciones(); // Actualizar contador también
      }
    });

    return () => remove();
  }, [addListener, fetchNotificaciones]);

  const handleAceptar = async (id) => {
    try {
      await aceptarSolicitud(id);
      await fetchSolicitudes();
      await fetchNotificaciones();
    } catch (err) {
      console.error("Error al aceptar:", err);
    }
  };

  const handleRechazar = async (id) => {
    try {
      await rechazarSolicitud(id);
      await fetchSolicitudes();
      await fetchNotificaciones();
    } catch (err) {
      console.error("Error al rechazar:", err);
    }
  };

  const handleProfileClick = (senderId) => {
    navigate(`/perfil/${senderId}`);
  };

  if (loading)
    return (
      <p className="text-center text-gray-500 mt-10">
        ⏳ {t("friend_requests.loading", "Cargando solicitudes...")}
      </p>
    );

  return (
    <div className="mx-auto mt-2 max-w-3xl px-4 pb-12 md:mt-10">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="mb-8 text-center">
        <h2 className="animate-[fadeInUp_0.6s_ease-out_both] text-2xl font-semibold tracking-tight text-[#245580] sm:text-3xl">
          {t("friend_requests.title", "Solicitudes de amistad")}
        </h2>
        <div className="mx-auto mt-3 h-1 w-14 rounded-full bg-[#247BA8]" />
      </div>

      {solicitudes.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-sky-100 bg-sky-50">
              <UsersRound size={26} strokeWidth={1.7} className="text-[#247BA8]" />
            </div>

            <p className="min-w-0 text-center text-base font-medium leading-relaxed text-slate-600">
              {t("friend_requests.none", "No tienes solicitudes pendientes.")}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {solicitudes.map((sol) => {
            const ciudad = sol.ciudad
              ? getCityLabel(sol.ciudad, sol.country)
              : "";

            const pais = sol.country
              ? getCountryLabel(sol.country)
              : "";

            const ubicacion = [ciudad, pais]
              .filter(Boolean)
              .join(", ");

            return (
              <div
                key={sol.id}
                onClick={() => handleProfileClick(sol.sender_id)}
                className="flex cursor-pointer flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:border-[#B8D8EA] hover:bg-[#F8FCFF] hover:shadow-md sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:p-5"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <img
                    src={sol.photo || "/default-avatar.png"}
                    alt={sol.nombre || "Usuario"}
                    className="h-14 w-14 shrink-0 rounded-full border border-slate-200 object-cover"
                  />

                  <div className="min-w-0">
                    <p className="break-words text-[17px] font-semibold leading-snug text-[#247BA8]">
                      {[sol.nombre, sol.apellidos]
                        .filter(Boolean)
                        .join(" ")}
                    </p>

                    {ubicacion && (
                      <p className="mt-1.5 flex items-start gap-1.5 text-sm leading-5 text-slate-500">
                        {getCountryFlag(sol.country) && (
                          <span
                            className="shrink-0 text-base leading-5"
                            aria-hidden="true"
                          >
                            {getCountryFlag(sol.country)}
                          </span>
                        )}
                        <span>{ubicacion}</span>
                      </p>
                    )}

                    {sol.edad != null && (
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                        <CalendarDays
                          size={15}
                          className="shrink-0 text-[#247BA8]"
                        />
                        <span>
                          {sol.edad}{" "}
                          {t("friend_requests.years", "años")}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div
                  className="flex w-full gap-2 sm:w-auto sm:shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => handleAceptar(sol.id)}
                    className="flex-1 whitespace-nowrap rounded-xl bg-[#16A34A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#15803D] sm:flex-none"
                  >
                    {t("friend_requests.accept", "Aceptar")}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRechazar(sol.id)}
                    className="flex-1 whitespace-nowrap rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 sm:flex-none"
                  >
                    {t("friend_requests.reject", "Rechazar")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Solicitudes;
